import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Sparkles, Trash2, Loader2, Crown } from 'lucide-react';
import { useAIChat } from '@/hooks/useAIChat';
import { useAICredits } from '@/hooks/useAICredits';
import { AIChatMessage } from './AIChatMessage';
import { AIChatCreditsInfo } from './AIChatCreditsInfo';
import { cn } from '@/lib/utils';

interface AIChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SUGGESTED_QUESTIONS = [
  "Ma configuration est-elle prête pour la production ?",
  "Combien de réservations ai-je aujourd'hui ?",
  "Comment configurer la demande d'avis ?",
  "Comment fonctionne le mode horaires ?",
  "Résumé de la semaine à venir",
];

export function AIChatDialog({ open, onOpenChange }: AIChatDialogProps) {
  const [input, setInput] = useState('');
  const { messages, isLoading, sendMessage, clearMessages } = useAIChat();
  const { creditsRemaining, creditsLimit, creditsBonus, isLoading: creditsLoading, refetch: refetchCredits } = useAICredits();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isPremium = (creditsLimit ?? 0) >= 50;
  const hasCredits = (creditsRemaining ?? 0) > 0;

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Refetch credits when dialog opens
  useEffect(() => {
    if (open) {
      refetchCredits();
    }
  }, [open, refetchCredits]);

  // Refetch credits after a message is sent
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      refetchCredits();
    }
  }, [isLoading, messages.length, refetchCredits]);

  const handleSend = () => {
    if (!input.trim() || isLoading || !hasCredits) return;
    sendMessage(input);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (question: string) => {
    if (!hasCredits) return;
    sendMessage(question);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] h-[80vh] max-h-[700px] flex flex-col p-0">
        <DialogHeader className="p-4 pb-2 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Assistant IA
              {!creditsLoading && creditsRemaining !== undefined && (
                <AIChatCreditsInfo 
                  creditsRemaining={creditsRemaining} 
                  creditsLimit={creditsLimit ?? 5} 
                  creditsBonus={creditsBonus ?? 0}
                  isPremium={isPremium}
                />
              )}
            </DialogTitle>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearMessages}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Effacer
              </Button>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {/* Credits exhausted message */}
          {!creditsLoading && !hasCredits && (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4 p-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Crédits épuisés</h3>
                <p className="text-muted-foreground text-sm max-w-sm">
                  Vous avez utilisé tous vos crédits IA pour ce mois.
                </p>
                {!isPremium && (
                  <p className="text-sm text-primary flex items-center justify-center gap-1">
                    <Crown className="h-4 w-4" />
                    Passez en Premium pour 50 crédits/mois
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Normal chat interface */}
          {(creditsLoading || hasCredits) && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Comment puis-je vous aider ?</h3>
                <p className="text-muted-foreground text-sm max-w-sm">
                  Posez-moi des questions sur vos réservations, vos statistiques ou demandez un résumé de votre activité.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-2 w-full max-w-sm">
                {SUGGESTED_QUESTIONS.map((question, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="text-left justify-start h-auto py-2 px-3 text-sm"
                    onClick={() => handleSuggestionClick(question)}
                    disabled={!hasCredits}
                  >
                    {question}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {(creditsLoading || hasCredits) && messages.length > 0 && (
            <div className="space-y-4">
              {messages.map((message, idx) => (
                <AIChatMessage key={idx} message={message} />
              ))}
              {isLoading && messages[messages.length - 1]?.role === 'user' && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Réflexion en cours...</span>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <div className="p-4 pt-2 border-t">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={hasCredits ? "Posez une question..." : "Crédits épuisés"}
              disabled={isLoading || !hasCredits}
              className="flex-1"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading || !hasCredits}
              size="icon"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
